import type {
  BlockRendererMapping,
  BlockValues,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { isChildrenProp } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import { useMemo } from "react";

import { useDeviceContext } from "../DeviceProvider";
import { isNonNullableUnknown } from "../Helpers/typePredicates";
import { flattenResponsiveStylesDeep } from "../Preview/flattenResponsiveStylesDeep";
import { RenderBlockList } from "./RenderBlockList";

export interface RendererProps {
  block: BaseBlock;
  components: BlockRendererMapping;
  path?: string;
}

export const Renderer = ({ block, components, path }: RendererProps) => {
  const device = useDeviceContext();

  // Memoize component lookup
  const Component = useMemo(
    () => components[block.type],
    [components, block.type],
  );

  if (!Component || block?.meta?.isHidden) return null;

  // Compute props without memoization to avoid React reconciliation issues.
  // `key` is intentionally not included here - it must be passed directly
  // on the JSX element below, not spread via a props object (React 19 warns
  // on this, since it can no longer statically tell the key apart from the
  // rest of the spread at the JSX callsite).
  const initialProps: BlockValues = {};

  const entries = Object.entries(block.props);

  const props = entries.reduce((props, [name, prop]) => {
    if (isChildrenProp(prop)) {
      props[name] = (
        <RenderBlockList
          blocks={prop.value}
          components={components}
          basePath={path ? `${path}.props.${name}.value` : undefined}
        />
      );
    } else if (isNonNullableUnknown(prop.value)) {
      if (name === "styles") {
        props[name] = flattenResponsiveStylesDeep(prop.value, device);
      } else {
        props[name] = prop.value;
      }
    }
    return props;
  }, initialProps);

  return (
    <Component
      key={block.id}
      data-block
      data-block-type={block.type}
      id={block.id}
      blockPath={path}
      blockInstanceMeta={block.meta}
      {...props}
    />
  );
};
